import { useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, ImagePlus, Wand2 } from "lucide-react";
import { API, getAuthHeaders } from "@/App";

const MAX_DIM = 512;

function fileToImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

// Simple background-removal: turn near-white pixels transparent.
// Works for product photos / stickers on white. For real BG removal users can
// pre-process externally.
function processImage(img, removeBg) {
  const ratio = Math.min(MAX_DIM / img.width, MAX_DIM / img.height, 1);
  const w = Math.round(img.width * ratio);
  const h = Math.round(img.height * ratio);
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const ctx = c.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  if (removeBg) {
    const data = ctx.getImageData(0, 0, w, h);
    const px = data.data;
    for (let i = 0; i < px.length; i += 4) {
      const r = px[i], g = px[i + 1], b = px[i + 2];
      // near-white background
      if (r > 235 && g > 235 && b > 235) {
        px[i + 3] = 0;
      } else if (r > 220 && g > 220 && b > 220) {
        // soft edge fade
        px[i + 3] = Math.max(0, px[i + 3] - 160);
      }
    }
    ctx.putImageData(data, 0, 0);
  }
  return c.toDataURL("image/png");
}

export default function StickerCreatorDialog({ open, onOpenChange, onCreated }) {
  const inputRef = useRef(null);
  const [name, setName] = useState("");
  const [preview, setPreview] = useState(null);
  const [removeBg, setRemoveBg] = useState(true);
  const [busy, setBusy] = useState(false);
  const [originalImg, setOriginalImg] = useState(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Imagen demasiado grande (max 8MB)"); return; }
    try {
      const img = await fileToImage(f);
      setOriginalImg(img);
      setPreview(processImage(img, removeBg));
      if (!name) setName(f.name.replace(/\.[^.]+$/, "").slice(0, 30));
    } catch {
      toast.error("No se pudo cargar la imagen");
    }
  };

  const toggleBg = () => {
    const next = !removeBg;
    setRemoveBg(next);
    if (originalImg) setPreview(processImage(originalImg, next));
  };

  const save = async () => {
    if (!preview) { toast.error("Elige una imagen"); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/stickers`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ name: name.trim() || "Sticker", image_url: preview }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Error");
      toast.success("Sticker creado");
      setPreview(null); setName(""); setOriginalImg(null);
      onCreated && onCreated();
    } catch (e) {
      toast.error(e.message || "No se pudo crear");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#17212b] border-[#232e3c] text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-pink-400" /> Crear sticker
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Sube una foto. Opcionalmente elimina el fondo blanco.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <input ref={inputRef} type="file" accept="image/*" hidden onChange={onFile} />
          <Button onClick={() => inputRef.current?.click()} className="w-full bg-[#232e3c] hover:bg-[#2d3947]">
            <ImagePlus className="w-4 h-4 mr-2" /> Elegir imagen
          </Button>

          <Input
            placeholder="Nombre del sticker"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="bg-[#0e1621] border-[#232e3c] text-white"
          />

          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={removeBg} onChange={toggleBg} />
            Quitar fondo blanco automáticamente
          </label>

          {preview && (
            <div className="rounded-lg p-3 flex items-center justify-center"
              style={{ backgroundImage: "linear-gradient(45deg, #2d3947 25%, transparent 25%), linear-gradient(-45deg, #2d3947 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #2d3947 75%), linear-gradient(-45deg, transparent 75%, #2d3947 75%)", backgroundSize: "16px 16px", backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0px" }}>
              <img src={preview} alt="preview" className="max-h-40" />
            </div>
          )}

          <Button onClick={save} disabled={!preview || busy} className="w-full bg-pink-600 hover:bg-pink-700">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
            Guardar sticker
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
